import React from "react";
import PropTypes from "prop-types";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus, faTrash } from "@fortawesome/free-solid-svg-icons";
import {
  Label,
  Input,
  Select,
  FormGroup,
  InputHelperText,
} from "../../../components/atoms/Form";
import { ConditionSetDBOptions } from "../constants/form-constants";

import DrugResponseItems from "../helpers/DrugResponseItems";

const DrugResponse = ({ data, setData, drugItems }) => {
  const handleDrugResponseChange = (e, index, key, secondaryIndex) => {
    const { value, name } = e.target;
    const newDrugResponse = [...data];
    if (key) {
      newDrugResponse[index][key][secondaryIndex][name] = value;
    } else {
      newDrugResponse[index][name] = value;
    }
    setData(newDrugResponse);
    drugItems.setValue(e, index, key, secondaryIndex);
  };

  const handleOnBlur = (e, index, key, conditionIndex) => {
    drugItems.setTouched(e, index, key, conditionIndex);

    // Force Rerender to see the new touched state
    const newDrugResponse = [...data];
    setData(newDrugResponse);
  };

  const handleAddDrugData = () => {
    setData([
      ...data,
      {
        db: "",
        id: "",
        drugName: "",
        condition: [{ id: "", db: "", name: "" }],
      },
    ]);
    drugItems.addItem();
  };

  const handleRemoveDrugData = (index) => {
    const dataCopy = [...data];
    dataCopy.splice(index, 1);
    setData(dataCopy);
    drugItems.removeItem(index);
  };

  const shouldAllowNewCondition = (index) => {
    return (
      data[index].condition.filter(({ db, id, name }) => name || (db && id))
        .length === data[index].condition.length
    );
  };

  const shouldAllowNewDrugResponse = (index) => {
    const otherInfo = data.map(({ db, id, drugName }) => ({
      db,
      id,
      drugName,
    }));

    const isConditionDataFilled = shouldAllowNewCondition(index);

    const isDrugResponseDataFilled =
      otherInfo.filter(({ db, id, drugName }) => drugName || (db && id))
        .length === otherInfo.length;

    return isConditionDataFilled && isDrugResponseDataFilled;
  };

  const handleAddCondition = (index) => {
    const dataCopy = [...data];
    dataCopy[index].condition.push({ id: "", db: "", name: "" });
    setData(dataCopy);
    drugItems.addCondition(index);
  };

  const handleRemoveCondition = (index, secondaryIndex) => {
    const dataCopy = [...data];
    dataCopy[index].condition.splice(secondaryIndex, 1);
    setData(dataCopy);
    drugItems.removeCondition(index, secondaryIndex);
  };

  return (
    <>
      <div className="tw-pt-8 tw-pb-8">
        <div className="tw-text-2xl">Drug Response</div>
        <div className="tw-text-sm">
          The drug Response evaluated specified by database ID or drug name, but
          not both{" "}
        </div>

        {data.map((x, index) => {
          const { databaseError, drugNameError, idError } =
            drugItems.getItemErrors(index);
          const { databaseTouched, idTouched, drugNameTouched } =
            drugItems.getItemTouched(index);

          const key = `${index}-drug-response-data`;
          return (
            <React.Fragment key={key}>
              <div className="tw-pt-6 tw-px-2 tw-flex tw-w-11/12">
                <div className="tw-w-4/12 tw-mr-4">
                  <Label text="Database" isRequired={true} />
                </div>
                <div className="tw-w-4/12 tw-mr-4">
                  <Label text="Id" isRequired={true} />
                </div>
                <div className="tw-w-4/12 tw-mr-4">
                  <Label text="Drug Name" isRequired={true} />
                </div>
              </div>
              <div className="tw-flex tw-flex-wrap tw-px-2 tw-py-4 tw-bg-gray-100">
                <div className="tw-flex tw-w-11/12">
                  <FormGroup className="tw-w-4/12 tw-mr-4" hasPadding={false}>
                    <Select
                      type="select"
                      disabled={x.drugName}
                      name="db"
                      onChange={(e) => handleDrugResponseChange(e, index)}
                      onBlur={(e) => handleOnBlur(e, index)}
                      value={x.db}
                    >
                      <option value="" />
                      {ConditionSetDBOptions.map((value, j) => (
                        <option
                          key={`${value}-${index}-${j}-drug-response-db-name`}
                          value={value}
                        >
                          {value}
                        </option>
                      ))}
                    </Select>
                    {databaseError && databaseTouched && (
                      <InputHelperText type="error">
                        {databaseError}
                      </InputHelperText>
                    )}
                  </FormGroup>
                  <FormGroup className="tw-w-4/12 tw-mr-4" hasPadding={false}>
                    <Input
                      disabled={x.drugName}
                      type="text"
                      name="id"
                      value={x.id}
                      onBlur={(e) => handleOnBlur(e, index)}
                      onChange={(e) => handleDrugResponseChange(e, index)}
                    />
                    {idError && idTouched && (
                      <InputHelperText type="error">{idError}</InputHelperText>
                    )}
                  </FormGroup>
                  <FormGroup className="tw-w-4/12 tw-mr-4" hasPadding={false}>
                    <Input
                      disabled={x.db || x.id}
                      type="text"
                      name="drugName"
                      value={x.drugName}
                      onBlur={(e) => handleOnBlur(e, index)}
                      onChange={(e) => handleDrugResponseChange(e, index)}
                    />
                    {drugNameError && drugNameTouched && (
                      <InputHelperText type="error">
                        {drugNameError}
                      </InputHelperText>
                    )}
                  </FormGroup>
                </div>
                <div className="tw-flex tw-w-11/12 tw-pt-4">
                  <div className="tw-w-4/12">
                    <p className="tw-text-white-100 tw-text-xl tw-ml-2">
                      Condition Data
                    </p>
                  </div>
                </div>
                <div className=" tw-px-2 tw-flex tw-w-11/12">
                  <div className="tw-w-4/12 tw-mr-4">
                    <Label text="Database" isRequired={true} />
                  </div>
                  <div className="tw-w-4/12 tw-mr-4">
                    <Label text="Id" isRequired={true} />
                  </div>
                  <div className="tw-w-4/12 tw-mr-4">
                    <Label text="Name" isRequired={true} />
                  </div>
                </div>
                {x.condition.map((y, conditionIndex) => {
                  const {
                    conditionDbError,
                    conditionIdError,
                    conditionNameError,
                  } = drugItems.getConditionErrors(index, conditionIndex);
                  const {
                    conditionDbTouched,
                    conditionIdTouched,
                    conditionNameTouched,
                  } = drugItems.getConditionTouched(index, conditionIndex);
                  return (
                    <React.Fragment
                      key={`condition-data-${index}-${conditionIndex}`}
                    >
                      <div className="tw-flex tw-w-full tw-bg-gray-200 tw-p-2">
                        <FormGroup
                          className="tw-w-4/12 tw-mr-4"
                          hasPadding={false}
                        >
                          <Select
                            type="select"
                            disabled={y.name}
                            name="db"
                            onBlur={(e) =>
                              handleOnBlur(
                                e,
                                index,
                                "condition",
                                conditionIndex
                              )
                            }
                            onChange={(e) =>
                              handleDrugResponseChange(
                                e,
                                index,
                                "condition",
                                conditionIndex
                              )
                            }
                            value={y.db}
                          >
                            <option value="" />
                            {ConditionSetDBOptions.map((value, idx) => (
                              <option
                                key={`${value}-${index}-${conditionIndex}-${idx}-drug-response-db-name-${idx}`}
                                value={value}
                              >
                                {value}
                              </option>
                            ))}
                          </Select>
                          {conditionDbError && conditionDbTouched && (
                            <InputHelperText type="error">
                              {conditionDbError}
                            </InputHelperText>
                          )}
                        </FormGroup>
                        <FormGroup
                          className="tw-w-4/12 tw-mr-4"
                          hasPadding={false}
                        >
                          <Input
                            disabled={y.name}
                            type="text"
                            name="id"
                            onBlur={(e) =>
                              handleOnBlur(
                                e,
                                index,
                                "condition",
                                conditionIndex
                              )
                            }
                            value={y.id}
                            onChange={(e) =>
                              handleDrugResponseChange(
                                e,
                                index,
                                "condition",
                                conditionIndex
                              )
                            }
                          />
                          {conditionIdError && conditionIdTouched && (
                            <InputHelperText type="error">
                              {conditionIdError}
                            </InputHelperText>
                          )}
                        </FormGroup>
                        <FormGroup
                          className="tw-w-4/12 tw-mr-4"
                          hasPadding={false}
                        >
                          <Input
                            disabled={y.db || y.id}
                            onBlur={(e) =>
                              handleOnBlur(
                                e,
                                index,
                                "condition",
                                conditionIndex
                              )
                            }
                            type="text"
                            name="name"
                            value={y.name}
                            onChange={(e) =>
                              handleDrugResponseChange(
                                e,
                                index,
                                "condition",
                                conditionIndex
                              )
                            }
                          />
                          {conditionNameError && conditionNameTouched && (
                            <InputHelperText type="error">
                              {conditionNameError}
                            </InputHelperText>
                          )}
                        </FormGroup>
                        <div className="tw-relative tw-w-1/12">
                          {x.condition.length !== 1 && (
                            <FontAwesomeIcon
                              icon={faTrash}
                              onClick={() =>
                                handleRemoveCondition(index, conditionIndex)
                              }
                              size="lg"
                              fixedWidth={true}
                              className="tw-px-1 tw-text-red-600 tw-cursor-pointer tw-absolute tw-top-1.5 tw-left-0"
                            />
                          )}
                        </div>
                      </div>
                      {x.condition.length - 1 === conditionIndex && (
                        <div className="tw-flex tw-m-4 tw-justify-center tw-w-11/12">
                          <button
                            type="button"
                            disabled={!shouldAllowNewCondition(index)}
                            onClick={() =>
                              handleAddCondition(index, conditionIndex)
                            }
                            className="btn btn-success"
                          >
                            <FontAwesomeIcon
                              icon={faPlus}
                              fixedWidth={true}
                              className="tw-px-1 tw-text-white-100"
                            />
                            Add Condition
                          </button>
                        </div>
                      )}
                    </React.Fragment>
                  );
                })}
              </div>

              <div className="tw-flex tw-m-4 tw-justify-center">
                {index === data.length - 1 && (
                  <button
                    type="button"
                    disabled={!shouldAllowNewDrugResponse(index)}
                    onClick={() => handleAddDrugData(index)}
                    className="btn btn-success"
                  >
                    <FontAwesomeIcon
                      icon={faPlus}
                      fixedWidth={true}
                      className="tw-px-1 tw-text-white-100"
                    />
                    Add Drug Response
                  </button>
                )}

                <div className="tw-flex tw-align-middle tw-items-center tw-justify-center tw-w-1/12">
                  {data.length !== 1 && (
                    <FontAwesomeIcon
                      icon={faTrash}
                      onClick={() => handleRemoveDrugData(index)}
                      size="lg"
                      fixedWidth={true}
                      className="tw-px-1 tw-text-red-600 tw-cursor-pointer"
                    />
                  )}
                </div>
              </div>
            </React.Fragment>
          );
        })}
      </div>
    </>
  );
};

DrugResponse.propTypes = {
  drugItems: PropTypes.instanceOf(DrugResponseItems).isRequired,
  setData: PropTypes.func.isRequired,
  data: PropTypes.arrayOf(
    PropTypes.shape({
      condition: PropTypes.arrayOf(
        PropTypes.shape({
          id: PropTypes.string,
          name: PropTypes.string,
          db: PropTypes.string,
        })
      ),
      id: PropTypes.string,
      db: PropTypes.string,
      drugName: PropTypes.string,
    })
  ).isRequired,
};

export default DrugResponse;
